import { z } from "zod";

/**
 * TikTok's Direct Post rules, as data both sides read ([32](../../../docs/32-what-each-network-requires.md)).
 *
 * TikTok's audit checks the posting screen against its Content Sharing Guidelines
 * (developers.tiktok.com/doc/content-sharing-guidelines, read 2026-09-26): the creator's name on the screen, a
 * privacy choice with **nothing selected**, comment/Duet/Stitch unticked and greyed out where the account has them
 * off, a commercial-content switch with TikTok's own words, and the consent sentence. The option names, their
 * explanations, the labels, the two warnings and the consent sentences are TikTok's, verbatim, because the audit
 * compares them; the switch's own title and hint, and the privacy prompt, are ours.
 *
 * One function, `tiktokRefusal`, says whether a set of choices may be posted. The Studio's dialog, the API and the
 * connector all ask it, so the three can never disagree about why a TikTok post cannot go.
 */

export const TIKTOK_PRIVACY = ["PUBLIC_TO_EVERYONE", "MUTUAL_FOLLOW_FRIENDS", "FOLLOWER_OF_CREATOR", "SELF_ONLY"] as const;
export type TikTokPrivacy = (typeof TIKTOK_PRIVACY)[number];

/** What each privacy level is called on the page — TikTok's own names for them in its app. */
export const TIKTOK_PRIVACY_WORDS: Record<TikTokPrivacy, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};

/**
 * What a person chose for one TikTok post. Nothing here is ever remembered from an account or defaulted: TikTok's
 * rules forbid it, and a post whose `privacyLevel` is missing is refused rather than guessed.
 */
export const TikTokChoices = z.object({
  privacyLevel: z.enum(TIKTOK_PRIVACY),
  allowComment: z.boolean().default(false),
  allowDuet: z.boolean().default(false),
  allowStitch: z.boolean().default(false),
  /** The commercial-content switch, off unless turned on. */
  disclose: z.boolean().default(false),
  /** "Your brand": promoting yourself or your own business. */
  yourBrand: z.boolean().default(false),
  /** "Branded content": promoting another brand or a third party. */
  brandedContent: z.boolean().default(false),
}).strict();
export type TikTokChoices = z.infer<typeof TikTokChoices>;

/** Who the post is going to, as TikTok's `creator_info` says at the moment of asking. */
export interface TikTokCreator {
  nickname: string;
  avatarUrl: string | null;
  /** The privacy levels this account may use today; the dropdown offers these and no others. */
  privacyOptions: TikTokPrivacy[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  /** The longest video this account may post, in seconds. */
  maxDurationSec: number | null;
  /** False when TikTok says the account cannot post right now (too many posts, a ban, the unaudited-app cap). */
  canPost: boolean;
  /** Why not, in the page's words, when `canPost` is false. */
  cannotPostBecause: string | null;
}

/** `GET /v1/channels/:id/tiktok`. */
export type TikTokCreatorResponse = TikTokCreator;

export const TIKTOK_TEXT = {
  disclose: "Disclose video content",
  discloseHint: "Turn on to disclose that this video promotes goods or services in exchange for something of value. Your video could promote yourself, a third party, or both.",
  yourBrand: "Your brand",
  yourBrandHint: "You are promoting yourself or your own business.",
  brandedContent: "Branded content",
  brandedContentHint: "You are promoting another brand or a third party.",
  chooseOne: "You need to indicate if your content promotes yourself, a third party, or both.",
  brandedNotPrivate: "Branded content visibility cannot be set to private.",
  processing: "After you post, it may take a few minutes for the video to process and be visible on your profile.",
  choosePrivacy: "Choose who can see this video — TikTok asks you every time.",
  cannotPost: "TikTok says this account cannot post right now. Try again later.",
} as const;

export const TIKTOK_MUSIC_USAGE_URL = "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
export const TIKTOK_BRANDED_CONTENT_URL = "https://www.tiktok.com/legal/page/global/bc-policy/en";

/** The label TikTok will put on the video, or null — the words the page shows once the switch is on. */
export function tiktokLabel(c: Pick<TikTokChoices, "disclose" | "yourBrand" | "brandedContent">): string | null {
  if (!c.disclose) return null;
  if (c.brandedContent) return "Your video will be labeled as 'Paid partnership'";
  if (c.yourBrand) return "Your video will be labeled as 'Promotional content'";
  return null;
}

/** The consent sentence under the button, TikTok's own; it names the Branded Content Policy only when it applies. */
export function tiktokConsent(c: Pick<TikTokChoices, "disclose" | "brandedContent">): string {
  return c.disclose && c.brandedContent
    ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
    : "By posting, you agree to TikTok's Music Usage Confirmation.";
}

/**
 * Why these choices may not be posted, in the page's words, or null when they may.
 *
 * Without a creator it checks what can be checked from the choices alone — what the API does on scheduling. With one
 * it checks them against the account too — what the dialog does as the person chooses and the connector does again
 * just before the upload, because an account can change between Monday's scheduling and Friday's post.
 */
export function tiktokRefusal(
  choices: unknown,
  creator?: TikTokCreator | null,
  video?: { durationMs?: number | null | undefined } | null,
): string | null {
  const parsed = TikTokChoices.safeParse(choices);
  if (!parsed.success) return TIKTOK_TEXT.choosePrivacy;
  const c = parsed.data;
  if (c.disclose && !c.yourBrand && !c.brandedContent) return TIKTOK_TEXT.chooseOne;
  if (c.disclose && c.brandedContent && c.privacyLevel === "SELF_ONLY") return TIKTOK_TEXT.brandedNotPrivate;
  if (!creator) return null;
  if (!creator.canPost) return creator.cannotPostBecause ?? TIKTOK_TEXT.cannotPost;
  if (!creator.privacyOptions.includes(c.privacyLevel)) {
    return `TikTok does not offer "${TIKTOK_PRIVACY_WORDS[c.privacyLevel]}" for this account.`;
  }
  if (c.allowComment && creator.commentDisabled) return "Comments are turned off on this TikTok account.";
  if (c.allowDuet && creator.duetDisabled) return "Duet is turned off on this TikTok account.";
  if (c.allowStitch && creator.stitchDisabled) return "Stitch is turned off on this TikTok account.";
  const ms = video?.durationMs ?? null;
  if (ms !== null && creator.maxDurationSec !== null && ms > creator.maxDurationSec * 1000) {
    return `This TikTok account can post videos up to ${creator.maxDurationSec} seconds; this one is ${Math.round(ms / 1000)}.`;
  }
  return null;
}
